import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { NavLink } from "react-router-dom";

import {
  Grid,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Button,
  Card as MuiCard,
  CardContent,
  CardHeader,
  Box,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Breadcrumbs as MuiBreadcrumbs,
  Link,
} from "@mui/material";
import { spacing } from "@mui/system";
import { green, red, blue, orange } from "@mui/material/colors";
import {
  Add as AddIcon,
  Event as EventIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarTodayIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationOnIcon,
  Email as EmailIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

import { axiosInstance } from "../../utils/axios";
import { useSpace } from "../../contexts/SpaceContext";
import { stripHtml } from "../../utils/HTMLParser";

// Charts
import Chart from "react-apexcharts";

const Divider = styled(MuiDivider)(spacing);
const Typography = styled(MuiTypography)(spacing);
const Card = styled(MuiCard)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

// Stats Card Component
const StatsCard = ({
  title,
  amount,
  icon,
  color,
  chip,
  percentagetext,
  percentagecolor,
}) => (
  <Card mb={6}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}
          >
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: chip ? 1 : 2 }}>
            {amount}
          </Typography>
          {chip && (
            <Chip
              size="small"
              label={chip}
              sx={{ fontSize: "0.75rem", mb: 0.5 }}
            />
          )}
          {percentagetext && (
            <Typography
              variant="caption"
              sx={{
                color: percentagecolor,
                fontWeight: 600,
                fontSize: "0.875rem",
                display: "block",
              }}
            >
              {percentagetext}
            </Typography>
          )}
        </Box>
        <Avatar
          sx={{
            backgroundColor: `${color}15`,
            color: color,
            width: 56,
            height: 56,
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

// Events Status Chart
const EventsStatusChart = ({ events }) => {
  const getEventCounts = () => {
    const now = new Date();
    let upcoming = 0,
      ongoing = 0,
      completed = 0;

    events.forEach((event) => {
      const startDate = new Date(parseInt(event.startDate));
      const endDate = new Date(parseInt(event.endDate));

      if (now < startDate) upcoming++;
      else if (now > endDate) completed++;
      else ongoing++;
    });

    return { upcoming, ongoing, completed };
  };

  const { upcoming, ongoing, completed } = getEventCounts();

  const chartOptions = {
    chart: {
      type: "pie",
      height: 300,
    },
    labels: ["Upcoming", "Ongoing", "Completed"],
    colors: [blue[500], orange[500], green[500]],
    legend: {
      position: "bottom",
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  };

  const series = [upcoming, ongoing, completed];

  return (
    <Card sx={{ height: "100%" }}>
      <CardHeader title="Events by Status" />
      <CardContent>
        <Chart options={chartOptions} series={series} type="pie" height={300} />
      </CardContent>
    </Card>
  );
};

// Events Timeline Chart
const EventsTimelineChart = ({ events }) => {
  const getMonthlyData = () => {
    const monthData = {};
    const currentYear = new Date().getFullYear();

    // Initialize 12 months
    for (let i = 0; i < 12; i++) {
      const monthKey = new Date(currentYear, i).toLocaleDateString("en-US", {
        month: "short",
      });
      monthData[monthKey] = 0;
    }

    events.forEach((event) => {
      const eventDate = new Date(parseInt(event.startDate));
      if (eventDate.getFullYear() === currentYear) {
        const monthKey = eventDate.toLocaleDateString("en-US", {
          month: "short",
        });
        if (monthData[monthKey] !== undefined) {
          monthData[monthKey]++;
        }
      }
    });

    return monthData;
  };

  const monthlyData = getMonthlyData();
  const categories = Object.keys(monthlyData);
  const data = Object.values(monthlyData);

  const chartOptions = {
    chart: {
      type: "area",
      height: 300,
      toolbar: { show: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    xaxis: { categories },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.3,
      },
    },
    colors: [blue[500]],
    grid: { strokeDashArray: 3 },
  };

  const series = [
    {
      name: "Events",
      data,
    },
  ];

  return (
    <Card sx={{ height: "100%" }}>
      <CardHeader title={`Events Timeline (${new Date().getFullYear()})`} />
      <CardContent>
        <Chart
          options={chartOptions}
          series={series}
          type="area"
          height={300}
        />
      </CardContent>
    </Card>
  );
};

// Attendance Chart
const AttendanceChart = ({ events }) => {
  const getAttendanceData = () => {
    return events
      .map((event) => ({
        name:
          event.name.length > 15
            ? event.name.substring(0, 15) + "..."
            : event.name,
        attendees: Array.isArray(event.attendees) ? event.attendees.length : 0,
      }))
      .slice(0, 10); // Top 10 events
  };

  const attendanceData = getAttendanceData();
  const categories = attendanceData.map((item) => item.name);
  const data = attendanceData.map((item) => item.attendees);

  const chartOptions = {
    chart: {
      type: "bar",
      height: 300,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
      },
    },
    dataLabels: { enabled: false },
    xaxis: { categories },
    colors: [green[500]],
    grid: { strokeDashArray: 3 },
  };

  const series = [
    {
      name: "Attendees",
      data,
    },
  ];

  return (
    <Card sx={{ height: "100%" }}>
      <CardHeader title="Top Events by Attendance" />
      <CardContent
        sx={{
          height: "calc(100% - 64px)",
          display: "flex",
          alignItems: "center",
        }}
      >
        {attendanceData.length > 0 ? (
          <Chart
            options={chartOptions}
            series={series}
            type="bar"
            height={300}
          />
        ) : (
          <Box textAlign="center" py={4} sx={{ width: "100%" }}>
            <Typography variant="body2" color="textSecondary">
              No attendance data available yet
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Recent Events Table
const RecentEventsTable = ({ events }) => {
  const recentEvents = events.slice(0, 5);

  const getEventStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(parseInt(startDate));
    const end = new Date(parseInt(endDate));

    if (now < start) return { status: "upcoming", color: blue[500] };
    if (now > end) return { status: "completed", color: green[500] };
    return { status: "ongoing", color: orange[500] };
  };

  return (
    <Card sx={{ height: "100%" }}>
      <CardHeader
        title="Recent Events"
        action={
          <Button
            component={RouterLink}
            to="/events/events"
            size="small"
            endIcon={<VisibilityIcon />}
          >
            View All
          </Button>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Event Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Attendees</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentEvents.map((event) => {
                const { status, color } = getEventStatus(
                  event.startDate,
                  event.endDate
                );
                const startDate = new Date(parseInt(event.startDate));

                return (
                  <TableRow key={event.id}>
                    <TableCell>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          {event.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {event.about
                            ? stripHtml(event.about).substring(0, 50) + "..."
                            : "No description"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {startDate.toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {event.timezone}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={status}
                        sx={{
                          backgroundColor: `${color}15`,
                          color: color,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {Array.isArray(event.attendees)
                        ? event.attendees.length
                        : 0}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={`/events/view-event/${event.id}`}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={`/events/edit-event/${event.id}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {recentEvents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ py: 3 }}
                    >
                      No events found. Create your first event to get started!
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

function EventsDashboard() {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentSpace } = useSpace();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    upcoming: 0,
    ongoing: 0,
    completed: 0,
    totalAttendees: 0,
    averageAttendance: 0,
    thisMonth: 0,
    organizers: new Set(),
    timezones: new Set(),
    completionRate: 0,
  });

  // Get spaceId from the context or URL
  const queryParams = new URLSearchParams(location.search);
  const spaceId = queryParams.get("spaceId") || currentSpace?.id;

  // Fetch events from API
  const fetchEvents = async () => {
    try {
      setLoading(true);

      if (!spaceId) {
        return;
      }

      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetEvents($spaceId: ID!) {
            getAllEvents(spaceId: $spaceId) {
              id
              name
              startDate
              endDate
              startTime
              endTime
              timezone
              about
              organizedBy
              supportEmail
              attendees {
                id
              }
            }
          }
        `,
        variables: {
          spaceId,
        },
      });

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      const fetchedEvents = response.data.getAllEvents || [];
      setEvents(fetchedEvents);

      // Calculate statistics
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      let upcoming = 0,
        ongoing = 0,
        completed = 0,
        totalAttendees = 0,
        thisMonth = 0;
      const organizers = new Set();
      const timezones = new Set();

      fetchedEvents.forEach((event) => {
        const startDate = new Date(parseInt(event.startDate));
        const endDate = new Date(parseInt(event.endDate));
        const attendeeCount = Array.isArray(event.attendees)
          ? event.attendees.length
          : 0;

        totalAttendees += attendeeCount;

        // Count events this month
        if (
          startDate.getMonth() === currentMonth &&
          startDate.getFullYear() === currentYear
        ) {
          thisMonth++;
        }

        // Collect organizers and timezones
        if (event.organizedBy) organizers.add(event.organizedBy);
        if (event.timezone) timezones.add(event.timezone);

        // Status counts
        if (now < startDate) upcoming++;
        else if (now > endDate) completed++;
        else ongoing++;
      });

      setStats({
        total: fetchedEvents.length,
        upcoming,
        ongoing,
        completed,
        totalAttendees,
        averageAttendance:
          fetchedEvents.length > 0
            ? Math.round(totalAttendees / fetchedEvents.length)
            : 0,
        thisMonth,
        organizers,
        timezones,
        completionRate:
          fetchedEvents.length > 0
            ? Math.round((completed / fetchedEvents.length) * 100)
            : 0,
      });
    } catch (error) {
      console.error("Failed to fetch events:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch events when the component mounts or when spaceId changes
  useEffect(() => {
    if (spaceId) {
      fetchEvents();
    }
  }, [spaceId]);

  return (
    <React.Fragment>
      <Helmet title="Events Dashboard" />

      <Grid justifyContent="space-between" container spacing={6}>
        <Grid item>
          <Typography variant="h3" gutterBottom>
            Events Dashboard
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/events/dashboard">
              Events
            </Link>
            <Typography>Dashboard</Typography>
          </Breadcrumbs>
        </Grid>

        <Grid item>
          <Button
            variant="contained"
            component={RouterLink}
            to="/events/add-event"
            startIcon={<AddIcon />}
          >
            Create Event
          </Button>
        </Grid>
      </Grid>

      <Divider my={6} />

      {/* Stats Cards Row 1 */}
      <Grid container spacing={6}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Events"
            amount={stats.total}
            icon={<EventIcon />}
            color={blue[600]}
            percentagetext={stats.total > 0 ? `${stats.upcoming} upcoming` : ""}
            percentagecolor={blue[600]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="This Month"
            amount={stats.thisMonth}
            icon={<CalendarTodayIcon />}
            color={green[600]}
            percentagetext={
              stats.total > 0
                ? `${Math.round(
                    (stats.thisMonth / stats.total) * 100
                  )}% of total`
                : ""
            }
            percentagecolor={green[600]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active Events"
            amount={stats.ongoing}
            icon={<TrendingUpIcon />}
            color={orange[600]}
            chip={stats.ongoing > 0 ? "Live Now" : "None Active"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Attendees"
            amount={stats.totalAttendees}
            icon={<PeopleIcon />}
            color={red[600]}
            percentagetext={
              stats.averageAttendance > 0
                ? `Avg: ${stats.averageAttendance} per event`
                : ""
            }
            percentagecolor={red[600]}
          />
        </Grid>
      </Grid>

      {/* Stats Cards Row 2 */}
      <Grid container spacing={6}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Completion Rate"
            amount={`${stats.completionRate}%`}
            icon={<ScheduleIcon />}
            color={stats.completionRate >= 50 ? green[600] : orange[600]}
            percentagetext={`${stats.completed} completed`}
            percentagecolor={
              stats.completionRate >= 50 ? green[600] : orange[600]
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Unique Organizers"
            amount={stats.organizers.size}
            icon={<PersonIcon />}
            color={blue[600]}
            percentagetext={
              stats.organizers.size > 1
                ? "Multiple organizers"
                : "Single organizer"
            }
            percentagecolor={blue[600]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Time Zones"
            amount={stats.timezones.size}
            icon={<LocationOnIcon />}
            color={green[600]}
            percentagetext={
              stats.timezones.size > 1 ? "Global events" : "Local events"
            }
            percentagecolor={green[600]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Avg Attendance"
            amount={stats.averageAttendance}
            icon={<EmailIcon />}
            color={orange[600]}
            percentagetext={
              stats.totalAttendees > 0
                ? `${stats.totalAttendees} total`
                : "No attendees yet"
            }
            percentagecolor={orange[600]}
          />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={6} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <EventsStatusChart events={events} />
        </Grid>
        <Grid item xs={12} md={8}>
          <EventsTimelineChart events={events} />
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={6}>
        <Grid item xs={12} md={8}>
          <RecentEventsTable events={events} />
        </Grid>
        <Grid item xs={12} md={4}>
          <AttendanceChart events={events} />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default EventsDashboard;
